import React, { useState } from "react";
import {
  Box, Button, Chip, Dialog, DialogActions, DialogContent, DialogTitle, Divider,
  Grid, MenuItem, Paper, Table, TableBody, TableCell, TableHead, TableRow,
  TextField, Tooltip, Typography, LinearProgress, Alert,
} from "@mui/material";
import {
  useGetDeviceAvailableReleasesQuery, useGetDeviceUpdateHistoryQuery,
  useAssignDeviceReleaseMutation, useInstallDeviceUpdateMutation,
  useApproveDeviceUpdateMutation, useScheduleDeviceUpdateMutation,
  usePauseDeviceUpdateMutation, useRetryDeviceUpdateMutation, useRollbackDeviceMutation,
} from "../../Api/attoDeskApi";
import { useNotifier } from "../../Core/Notifier";
import PageLoader from "../Loader/PageLoader";
import {
  UpdateStatusChip, PolicyChip, CHANNEL_META, formatBytes, fmtDate,
  POLICIES, INSTALL_MODES,
} from "../../Pages/AppUpdates/updateShared";

const NAVY = "#0f172a";
const BORDER = "#e2e8f0";
const MUTED = "#64748b";

const Info: React.FC<{ label: string; value: React.ReactNode }> = ({ label, value }) => (
  <Grid item xs={6} sm={4}>
    <Typography sx={{ fontSize: 10, fontWeight: 700, color: MUTED, textTransform: "uppercase", letterSpacing: "0.04em" }}>{label}</Typography>
    <Box sx={{ fontSize: 13, color: NAVY, fontWeight: 600, mt: 0.25 }}>{value ?? "—"}</Box>
  </Grid>
);

const DeviceReleasesPanel: React.FC<{ deviceId: number }> = ({ deviceId }) => {
  const { showMessage, showErrorMessage } = useNotifier();
  const { data, isLoading, refetch } = useGetDeviceAvailableReleasesQuery(deviceId);
  const { data: historyData, refetch: refetchHistory } = useGetDeviceUpdateHistoryQuery(deviceId);

  const [assign] = useAssignDeviceReleaseMutation();
  const [installNow] = useInstallDeviceUpdateMutation();
  const [approve] = useApproveDeviceUpdateMutation();
  const [schedule] = useScheduleDeviceUpdateMutation();
  const [pause] = usePauseDeviceUpdateMutation();
  const [retry] = useRetryDeviceUpdateMutation();
  const [rollback] = useRollbackDeviceMutation();

  // Assign / schedule dialog state.
  const [dlg, setDlg] = useState<null | { release: any; mode: "assign" | "schedule" }>(null);
  const [dPolicy, setDPolicy] = useState("optional");
  const [dInstall, setDInstall] = useState("maintenance_window");
  const [dAt, setDAt] = useState("");

  if (isLoading) return <PageLoader height="20vh" />;
  const summary: any = (data?.data as any)?.device || {};
  const releases: any[] = (data?.data as any)?.releases || [];
  const history: any[] = Array.isArray(historyData?.data) ? (historyData?.data as any[]) : [];

  const refreshAll = () => { refetch(); refetchHistory(); };
  const run = async (fn: () => Promise<any>, ok: string) => {
    try {
      const res: any = await fn();
      if (res?.status ?? true) { showMessage(ok); refreshAll(); }
      else showErrorMessage(res?.message || "Failed");
    } catch (e: any) { showErrorMessage(e?.data?.message || "Failed"); }
  };

  const openAssign = (release: any, mode: "assign" | "schedule") => {
    setDPolicy("optional"); setDInstall("maintenance_window"); setDAt("");
    setDlg({ release, mode });
  };

  const submitDlg = async () => {
    if (!dlg) return;
    const body: any = { release_id: dlg.release.id, policy: dPolicy, install_mode: dInstall };
    if (dInstall === "scheduled" && dAt) body.scheduled_at = new Date(dAt).toISOString();
    try {
      const res: any = await assign({ id: deviceId, body }).unwrap();
      if (res.status) { showMessage("Release assigned"); setDlg(null); refreshAll(); }
      else showErrorMessage(res.message || "Failed");
    } catch (e: any) { showErrorMessage(e?.data?.message || e?.data?.reasons?.join(", ") || "Failed"); }
  };

  const hasAssignment = !!summary.update_status &&
    !["up_to_date", "update_available"].includes(summary.update_status);
  const awaiting = summary.update_status === "awaiting_approval";

  return (
    <Box>
      {/* Current update state */}
      <Paper elevation={0} sx={{ border: `1px solid ${BORDER}`, borderRadius: 2.5, p: 2.5, mb: 2 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 1.5 }}>
          <Typography sx={{ fontSize: 14, fontWeight: 800, color: NAVY }}>Update status</Typography>
          <UpdateStatusChip status={summary.update_status} />
          <PolicyChip policy={summary.update_policy} />
          <Box sx={{ flex: 1 }} />
        </Box>
        {typeof summary.update_progress === "number" && summary.update_progress > 0 && summary.update_progress < 100 && (
          <Box sx={{ mb: 1.5 }}>
            <LinearProgress variant="determinate" value={summary.update_progress} sx={{ borderRadius: 1, height: 6 }} />
            <Typography sx={{ fontSize: 11, color: MUTED, mt: 0.5 }}>{summary.update_progress}% downloaded</Typography>
          </Box>
        )}
        <Grid container spacing={2}>
          <Info label="Installed version" value={summary.installed_version || summary.app_version || "—"} />
          <Info label="Assigned release" value={summary.assigned_version || "—"} />
          <Info label="Update channel" value={summary.update_channel || "—"} />
          <Info label="Install mode" value={summary.update_install_mode || "—"} />
          <Info label="Last update check" value={fmtDate(summary.last_update_check_at)} />
          <Info label="Last update attempt" value={fmtDate(summary.last_update_attempt_at)} />
        </Grid>

        <Divider sx={{ my: 1.5 }} />
        <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
          <Button size="small" variant="outlined" disabled={!hasAssignment}
            onClick={() => run(() => installNow(deviceId).unwrap(), "Install triggered")} sx={{ textTransform: "none" }}>Install now</Button>
          {awaiting && (
            <Button size="small" variant="contained" disableElevation
              onClick={() => run(() => approve(deviceId).unwrap(), "Approved")} sx={{ textTransform: "none" }}>Approve</Button>
          )}
          <Button size="small" variant="outlined" disabled={!hasAssignment}
            onClick={() => run(() => pause(deviceId).unwrap(), "Update paused")} sx={{ textTransform: "none" }}>Pause</Button>
          <Button size="small" variant="outlined" disabled={!hasAssignment}
            onClick={() => run(() => retry(deviceId).unwrap(), "Retry triggered")} sx={{ textTransform: "none" }}>Retry</Button>
        </Box>
      </Paper>

      {/* Compatible releases */}
      <Paper elevation={0} sx={{ border: `1px solid ${BORDER}`, borderRadius: 2.5, overflow: "hidden", mb: 2 }}>
        <Typography sx={{ fontSize: 14, fontWeight: 800, color: NAVY, p: 2, pb: 1 }}>Available releases</Typography>
        <Table size="small">
          <TableHead>
            <TableRow>
              {["Version", "Channel", "Released", "Compatibility", "Status", "Actions"].map((h, i) => (
                <TableCell key={h} align={i === 5 ? "right" : "left"}
                  sx={{ fontSize: 10, fontWeight: 700, color: MUTED, textTransform: "uppercase" }}>{h}</TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {releases.length === 0 ? (
              <TableRow><TableCell colSpan={6} sx={{ textAlign: "center", py: 4, color: MUTED }}>No compatible releases.</TableCell></TableRow>
            ) : releases.map((rel) => {
              const cc = CHANNEL_META[rel.channel] || CHANNEL_META.stable;
              const isDowngrade = summary.app_version_code && rel.version_code < summary.app_version_code;
              return (
                <TableRow key={rel.id} hover>
                  <TableCell>
                    <Typography sx={{ fontSize: 13, fontWeight: 700, color: NAVY }}>
                      {rel.version_name}{" "}
                      {rel.is_installed && <Chip size="small" label="installed" sx={{ height: 16, fontSize: 9, bgcolor: "#ecfdf5", color: "#047857" }} />}
                      {rel.is_assigned && <Chip size="small" label="assigned" sx={{ height: 16, fontSize: 9, bgcolor: "#eff6ff", color: "#1d4ed8" }} />}
                    </Typography>
                    <Typography sx={{ fontSize: 10, color: MUTED, fontFamily: "monospace" }}>
                      code {rel.version_code} · {formatBytes(rel.file_size)}
                    </Typography>
                  </TableCell>
                  <TableCell><Chip size="small" label={String(rel.channel).toUpperCase()} sx={{ height: 18, fontSize: 9, fontWeight: 700, bgcolor: cc.bg, color: cc.fg }} /></TableCell>
                  <TableCell sx={{ fontSize: 11, color: MUTED }}>{rel.created_at ? new Date(rel.created_at).toLocaleDateString() : "—"}</TableCell>
                  <TableCell>
                    {rel.compatible ? (
                      <Chip size="small" label="Compatible" sx={{ height: 18, fontSize: 9, bgcolor: "#ecfdf5", color: "#047857" }} />
                    ) : (
                      <Tooltip title={(rel.compat_reasons || []).join("; ")}>
                        <Chip size="small" label="Incompatible" sx={{ height: 18, fontSize: 9, bgcolor: "#fef2f2", color: "#b91c1c" }} />
                      </Tooltip>
                    )}
                  </TableCell>
                  <TableCell><UpdateStatusChip status={rel.assignment_status} /></TableCell>
                  <TableCell align="right">
                    <Box sx={{ display: "flex", gap: 0.5, justifyContent: "flex-end", flexWrap: "wrap" }}>
                      {isDowngrade ? (
                        <Button size="small" variant="outlined" color="warning"
                          onClick={() => run(() => rollback({ id: deviceId, release_id: rel.id }).unwrap(), "Rolling back")}
                          sx={{ textTransform: "none", fontSize: 11 }}>Roll back</Button>
                      ) : (
                        <>
                          <Button size="small" variant="outlined" disabled={!rel.compatible}
                            onClick={() => openAssign(rel, "assign")} sx={{ textTransform: "none", fontSize: 11 }}>Assign</Button>
                          <Button size="small" variant="outlined" disabled={!rel.compatible}
                            onClick={() => run(() => assign({ id: deviceId, body: { release_id: rel.id, policy: "automatic", install_mode: "immediate" } }).unwrap(), "Installing")}
                            sx={{ textTransform: "none", fontSize: 11 }}>Install now</Button>
                        </>
                      )}
                    </Box>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </Paper>

      {/* History */}
      <Paper elevation={0} sx={{ border: `1px solid ${BORDER}`, borderRadius: 2.5, p: 2.5 }}>
        <Typography sx={{ fontSize: 14, fontWeight: 800, color: NAVY, mb: 1 }}>Update history</Typography>
        {history.length === 0 ? (
          <Typography sx={{ fontSize: 13, color: MUTED }}>No update events yet.</Typography>
        ) : (
          <Box sx={{ maxHeight: 320, overflow: "auto" }}>
            {history.map((e) => (
              <Box key={e.id} sx={{ display: "flex", gap: 1.5, py: 0.75, borderBottom: `1px solid ${BORDER}` }}>
                <Typography sx={{ fontSize: 11, color: MUTED, minWidth: 130 }}>{fmtDate(e.created_at)}</Typography>
                <Chip size="small" label={e.event_type} sx={{ height: 18, fontSize: 9 }} />
                <Typography sx={{ fontSize: 12, color: NAVY }}>
                  {e.version_name ? `v${e.version_name} ` : ""}{e.message || ""}
                </Typography>
              </Box>
            ))}
          </Box>
        )}
      </Paper>

      {/* Assign / schedule dialog */}
      <Dialog open={!!dlg} onClose={() => setDlg(null)} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle sx={{ fontSize: 15, fontWeight: 800, color: NAVY }}>
          Assign {dlg?.release?.version_name}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: "grid", gap: 2, mt: 1 }}>
            <TextField select size="small" label="Update policy" value={dPolicy} onChange={(e) => setDPolicy(e.target.value)}>
              {POLICIES.map((p) => <MenuItem key={p.value} value={p.value}>{p.label}</MenuItem>)}
            </TextField>
            <TextField select size="small" label="Installation" value={dInstall} onChange={(e) => setDInstall(e.target.value)}>
              {INSTALL_MODES.map((m) => <MenuItem key={m.value} value={m.value}>{m.label}</MenuItem>)}
            </TextField>
            {dInstall === "scheduled" && (
              <TextField size="small" type="datetime-local" label="Install at" InputLabelProps={{ shrink: true }}
                value={dAt} onChange={(e) => setDAt(e.target.value)} />
            )}
            {dPolicy === "mandatory" && <Alert severity="warning" sx={{ fontSize: 12 }}>Mandatory: the device must update to this version.</Alert>}
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setDlg(null)} sx={{ textTransform: "none" }}>Cancel</Button>
          <Button variant="contained" disableElevation onClick={submitDlg} sx={{ textTransform: "none", fontWeight: 700 }}>Assign</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default DeviceReleasesPanel;
